// David Dobrik's channel ID
export const DAVID_DOBRIK_CHANNEL_ID = 'UCmh5gdwCx6lN7gEC20leNVA';

// Load video data from API fetch
let videoDataCache = null;

// Function to load video data from the JSON file
async function loadVideoData() {
  if (videoDataCache) {
    return videoDataCache;
  }

  try {
    const response = await fetch('/dobrik_videos.json');
    const data = await response.json();
    videoDataCache = data;
    return data;
  } catch (error) {
    console.error('Failed to load video data:', error);
    // Fallback to a few hardcoded videos if the JSON file fails to load
    return {
      videos: [
        {
          id: '29_omo0QGXw',
          title: 'KISSING A GIRL FOR THE FIRST TIME !!',
          embeddable: true,
          era: '2023–Now Comeback'
        },
        {
          id: 'hbAnW2MhExc',
          title: 'SURPRISE DREAM VACATION !!',
          embeddable: true,
          era: '2023–Now Comeback'
        },
        {
          id: 'S8nNhY-XVXU',
          title: 'SURPRISING FRIENDS WITH BODY TRANSFORMATION!!',
          embeddable: true,
          era: '2023–Now Comeback'
        }
      ]
    };
  }
}

// Get all embeddable video IDs
export async function getVideoIds() {
  const data = await loadVideoData();
  return data.videos
    .filter(video => video.embeddable && video.privacyStatus === 'public')
    .map(video => video.id);
}

// Get all video data with metadata
export async function getAllVideoData() {
  const data = await loadVideoData();
  return data.videos.filter(video => video.embeddable && video.privacyStatus === 'public');
}

// Get video metadata by ID
export async function getVideoMetadata(videoId) {
  const data = await loadVideoData();
  return data.videos.find(video => video.id === videoId);
}

// Legacy export for backwards compatibility - now async
export const videoIds = [
  // Fallback IDs while the async data loads
  '29_omo0QGXw', // KISSING A GIRL FOR THE FIRST TIME !!
  'hbAnW2MhExc', // SURPRISE DREAM VACATION !!
  'S8nNhY-XVXU', // SURPRISING FRIENDS WITH BODY TRANSFORMATION!!
  '-b1WnjKvcUg', // HOT TUB WITH INTERNATIONAL MODEL!!
  '-bpkiObJMCY', // SURPRISE MADE HER START CRYING!!
];

// Initialize the video data cache on module load
loadVideoData().then(data => {
  // Replace the fallback videoIds with the real ones once loaded
  const embeddableIds = data.videos
    .filter(video => video.embeddable && video.privacyStatus === 'public')
    .map(video => video.id);
  
  // Clear the array and add all the real IDs
  videoIds.length = 0;
  videoIds.push(...embeddableIds);
  
  console.log(`Loaded ${videoIds.length} embeddable David Dobrik videos from API`);
}).catch(error => {
  console.error('Failed to initialize video data:', error);
});

// Browser cache management for played videos
const CACHE_KEY = 'ddvlogs_played_videos';
const CACHE_EXPIRY_DAYS = 7; // Cache expires after 7 days

// Get played videos from browser cache
function getPlayedVideos() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return {};
    
    const data = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (data.timestamp && (now - data.timestamp) > (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)) {
      localStorage.removeItem(CACHE_KEY);
      return {};
    }
    
    return data.videos || {};
  } catch (error) {
    console.error('Error reading played videos cache:', error);
    return {};
  }
}

// Save played videos to browser cache
function savePlayedVideos(playedVideos) {
  try {
    const data = {
      videos: playedVideos,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving played videos cache:', error);
  }
}

// Mark a video as played
export function markVideoAsPlayed(videoId) {
  const playedVideos = getPlayedVideos();
  const now = Date.now();
  
  if (!playedVideos[videoId]) {
    playedVideos[videoId] = {
      playCount: 0,
      lastPlayed: now,
      firstPlayed: now
    };
  }
  
  playedVideos[videoId].playCount += 1;
  playedVideos[videoId].lastPlayed = now;
  
  savePlayedVideos(playedVideos);
}

// Calculate weight for video selection (lower = more likely to be selected)
function calculateVideoWeight(videoId, playedVideos) {
  const videoData = playedVideos[videoId];
  
  if (!videoData) {
    return 1; // New videos have lowest weight (highest priority)
  }
  
  const { playCount, lastPlayed } = videoData;
  const now = Date.now();
  const daysSinceLastPlayed = (now - lastPlayed) / (24 * 60 * 60 * 1000);
  
  // Base weight increases with play count
  let weight = 1 + (playCount * 2);
  
  // Reduce weight as time passes since last played (videos become "fresher")
  // After 1 day: 50% weight reduction, after 3 days: 75% reduction, after 7 days: 90% reduction
  const timeReduction = Math.min(0.9, daysSinceLastPlayed / 7 * 0.9);
  weight = weight * (1 - timeReduction);
  
  // Ensure minimum weight of 0.1 to keep some variety
  return Math.max(0.1, weight);
}

// Weighted random selection
function weightedRandomSelection(videoIds, weights) {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < videoIds.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return videoIds[i];
    }
  }
  
  // Fallback to last video if something goes wrong
  return videoIds[videoIds.length - 1];
}

// Get a random video ID from the complete collection with weighted selection
export function getRandomVideoId() {
  if (videoIds.length === 0) {
    return null;
  }
  
  const playedVideos = getPlayedVideos();
  
  // Calculate weights for all videos
  const weights = videoIds.map(videoId => {
    const baseWeight = calculateVideoWeight(videoId, playedVideos);
    // Add some randomness to prevent the same "fresh" video from always being picked
    const randomFactor = 0.5 + (Math.random() * 0.5); // Between 0.5 and 1.0
    return baseWeight * randomFactor;
  });
  
  // Use weighted selection
  return weightedRandomSelection(videoIds, weights);
}

// Get weighted random video with async support for full video data
export async function getWeightedRandomVideo(eraFilter = null) {
  const allVideos = await getAllVideoData();
  if (allVideos.length === 0) {
    return null;
  }
  
  // Filter by era if specified
  const filteredVideos = eraFilter 
    ? allVideos.filter(video => video.era === eraFilter)
    : allVideos;
  
  if (filteredVideos.length === 0) {
    console.warn(`No videos found for era: ${eraFilter}`);
    return null;
  }
  
  const playedVideos = getPlayedVideos();
  const videoIds = filteredVideos.map(v => v.id);
  
  // Calculate weights
  const weights = videoIds.map(videoId => {
    const baseWeight = calculateVideoWeight(videoId, playedVideos);
    const randomFactor = 0.5 + (Math.random() * 0.5);
    return baseWeight * randomFactor;
  });
  
  const selectedId = weightedRandomSelection(videoIds, weights);
  return filteredVideos.find(v => v.id === selectedId);
}

// Get all available eras from the video data
export async function getAvailableEras() {
  const data = await getAllVideoData();
  const eras = [...new Set(data.map(video => video.era))].sort();
  return eras;
}

// Get era-filtered random video ID (backwards compatibility)
export async function getRandomVideoIdByEra(eraFilter = null) {
  const video = await getWeightedRandomVideo(eraFilter);
  return video ? video.id : null;
}

// Get play statistics for debugging/analytics
export function getPlayStatistics() {
  const playedVideos = getPlayedVideos();
  const totalPlays = Object.values(playedVideos).reduce((sum, data) => sum + data.playCount, 0);
  const uniqueVideosPlayed = Object.keys(playedVideos).length;
  
  return {
    totalPlays,
    uniqueVideosPlayed,
    totalAvailable: videoIds.length,
    percentageWatched: videoIds.length > 0 ? (uniqueVideosPlayed / videoIds.length * 100).toFixed(1) : 0,
    playedVideos
  };
}

// Clear play history (for testing/debugging)
export function clearPlayHistory() {
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log('Play history cleared successfully');
    return true;
  } catch (error) {
    console.error('Error clearing play history:', error);
    return false;
  }
}

// Debug function to log current play weights
export function debugVideoWeights() {
  if (videoIds.length === 0) {
    console.log('No videos loaded yet');
    return;
  }
  
  const playedVideos = getPlayedVideos();
  const weights = videoIds.map(videoId => {
    const weight = calculateVideoWeight(videoId, playedVideos);
    const playData = playedVideos[videoId];
    return {
      id: videoId,
      weight: weight.toFixed(3),
      playCount: playData ? playData.playCount : 0,
      lastPlayed: playData ? new Date(playData.lastPlayed).toLocaleDateString() : 'Never'
    };
  });
  
  // Sort by weight (ascending = higher priority)
  weights.sort((a, b) => parseFloat(a.weight) - parseFloat(b.weight));
  
  console.log('Video Selection Weights (lower = more likely to be selected):');
  console.table(weights.slice(0, 20)); // Show top 20
}

// Function to get videos by era
export async function getVideosByEra(era) {
  const data = await getAllVideoData();
  return data.filter(video => video.era === era);
}

// Function to get most recent videos
export async function getRecentVideos(count = 20) {
  const data = await getAllVideoData();
  return data
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, count);
}

// Function to get random sample with metadata
export async function getRandomSample(count = 10) {
  const data = await getAllVideoData();
  const shuffled = [...data].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

// Get statistics about the video collection
export async function getVideoStats() {
  const data = await loadVideoData();
  const embeddableVideos = data.videos.filter(video => video.embeddable && video.privacyStatus === 'public');
  
  const eras = {};
  embeddableVideos.forEach(video => {
    eras[video.era] = (eras[video.era] || 0) + 1;
  });

  return {
    total: embeddableVideos.length,
    totalFetched: data.total,
    embeddable: data.counts.embeddable,
    nonEmbeddable: data.counts.nonEmbeddable,
    byEra: eras,
    fetchedAt: data.fetchedAt
  };
}

export default videoIds;